import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Heart, ShoppingCart } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useCart } from "@/hooks/useCart";
import { useToast } from "@/hooks/use-toast";
import { useDatabaseFavorites } from "@/hooks/useDatabaseFavorites";
import { highlightMatches, clearHighlights } from "@/utils/searchHighlight";
import { toProxiedImageUrl } from "@/lib/imageOptimizer";
import type { Product } from "@/types/product";

interface ProductCardProps {
  product: Product;
  priority?: boolean;
  index?: number;
}

function getOptimizedImageUrl(url: string, width = 400, quality = 75): string {
  // Route through image-proxy-worker for real browser/edge caching AND (now that the
  // worker applies Cloudflare Image Resizing on width/quality params) a card-sized
  // variant instead of the full 800×800 original. Falls back to the original if the
  // zone's Transformations toggle is off.
  // NOTE: a similarly-named getOptimizedImageUrl also exists in src/lib/imageOptimizer.ts (different signature/behaviour) — not consolidated with this one, see that file before editing either.
  return toProxiedImageUrl(url, { width, quality }) || "/placeholder.svg";
}

function flyToCart(imgEl: HTMLImageElement) {
  const isMobile = window.innerWidth < 768;
  const cartEl = document.getElementById(isMobile ? 'mobile-cart-target' : 'desktop-cart-target');
  if (!cartEl) return;
  const imgRect = imgEl.getBoundingClientRect();
  const cartRect = cartEl.getBoundingClientRect();
  const clone = document.createElement('img');
  clone.src = imgEl.src;
  clone.style.cssText = `position:fixed;top:${imgRect.top}px;left:${imgRect.left}px;width:${imgRect.width}px;height:${imgRect.height}px;border-radius:8px;object-fit:cover;z-index:9999;pointer-events:none;`;
  document.body.appendChild(clone);
  const tx = cartRect.left + cartRect.width / 2;
  const ty = cartRect.top + cartRect.height / 2;
  clone.animate([
    { top: `${imgRect.top}px`, left: `${imgRect.left}px`, width: `${imgRect.width}px`, height: `${imgRect.height}px`, opacity: '1', borderRadius: '8px' },
    { top: `${imgRect.top + (ty - imgRect.top) * 0.4}px`, left: `${imgRect.left + (tx - imgRect.left) * 0.2}px`, width: `${imgRect.width * 0.6}px`, height: `${imgRect.height * 0.6}px`, opacity: '0.85', borderRadius: '50%', offset: 0.45 },
    { top: `${ty - 12}px`, left: `${tx - 12}px`, width: '24px', height: '24px', opacity: '0', borderRadius: '50%' },
  ], { duration: 650, easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)', fill: 'forwards' }).onfinish = () => {
    clone.remove();
    cartEl.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.5)' }, { transform: 'scale(0.9)' }, { transform: 'scale(1)' }], { duration: 350, easing: 'ease-out' });
  };
}

const ProductCard = ({ product, priority = false, index = 0 }: ProductCardProps) => {
  const { addToCart } = useCart();
  const { toast } = useToast();
  const { isFavorite, toggleFavorite } = useDatabaseFavorites();
  // Image fallback: 0 = resized proxy URL, 1 = raw image_url, 2 = placeholder
  // (same order as product-detail/ProductImageGallery.tsx).
  const [imageStep, setImageStep] = useState(0);
  const cardRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const location = useLocation();

  // Highlight search matches
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const searchTerm = searchParams.get("search");
    if (cardRef.current) {
      clearHighlights(cardRef.current);
      if (searchTerm) {
        highlightMatches(cardRef.current, searchTerm);
      }
    }
  }, [location.search]);

  // Calculate display price
  let displayPrice = product.incl_price;
  let originalPrice = null;
  if (product.is_special_discount && product.special_price) {
    displayPrice = product.special_price;
    originalPrice = product.incl_price;
  }

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (imgRef.current) flyToCart(imgRef.current);
    addToCart(product.id, 1);
    toast({
      title: "Added to cart",
      description: `${product.product_name} added to your cart`,
    });
  };

  const handleToggleFavorite = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavorite(product.id);
    toast({
      title: isFavorite(product.id) ? "Removed from favorites" : "Added to favorites",
      description: `${product.product_name} ${isFavorite(product.id) ? "removed from" : "added to"} your favorites`,
    });
  };

  // Resized proxy URL first; on error the raw file, then the placeholder.
  const rawImageUrl = product.product_images?.[0]?.image_url;
  const productImage =
    !rawImageUrl || imageStep >= 2
      ? "/placeholder.svg"
      : imageStep === 1
        ? rawImageUrl
        : getOptimizedImageUrl(rawImageUrl, 400, 75);

  // Loading strategy for LCP optimisation — only the priority (first-row) cards
  // load eagerly; everything else is lazy so below-the-fold cards don't compete
  // with the LCP image.
  const shouldLazyLoad = !priority;
  const fetchPriority = priority || index < 2 ? "high" : "auto";

  return (
    <Link to={`/products/${product.ws_code}`} state={{ productPreview: product }} className="block h-full">
      <Card
        ref={cardRef}
        className="group hover:shadow-lg transition-all duration-300 border-gray-200 hover:border-springbok-300 h-full flex flex-col"
      >
        <div className="relative aspect-square w-full overflow-hidden rounded-t-lg">
          <img
            ref={imgRef}
            src={productImage}
            alt={product.product_name || "Product image"}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={() => setImageStep((s) => Math.min(s + 1, 2))}
            loading={shouldLazyLoad ? "lazy" : "eager"}
            {...({ fetchpriority: fetchPriority } as any)}
            decoding="async"
            width={800}
            height={800}
          />

          {/* Badges */}
          <div className="absolute top-2 left-2 flex flex-col gap-1">
            {product.is_new_arrival && (
              <Badge variant="secondary" className="text-xs">
                New
              </Badge>
            )}
            {product.is_top_seller && (
              <Badge variant="secondary" className="text-xs rounded-none bg-[#f51313]">
                Best Seller
              </Badge>
            )}
            {product.is_special_discount && (
              <Badge variant="destructive" className="text-xs">
                Special
              </Badge>
            )}
            {!product.stock_available && (
              <Badge variant="destructive" className="text-xs">
                Out of Stock
              </Badge>
            )}
          </div>

          {/* Favourite button */}
          <button
            onClick={handleToggleFavorite}
            className="absolute top-2 right-2 p-2 rounded-full bg-white/80 hover:bg-white transition-colors"
          >
            <Heart
              size={16}
              className={`transition-colors ${
                isFavorite(product.id) ? "fill-current text-springbok-500" : "text-gray-600 hover:text-springbok-500"
              }`}
            />
          </button>
        </div>

        <CardContent className="p-4 flex-1 flex flex-col justify-between">
          <div className="space-y-2 flex-1">
            <h3 className="font-semibold text-xs sm:text-sm line-clamp-3 group-hover:text-springbok-600 transition-colors leading-tight">
              {product.website_title?.trim() || product.product_name?.trim()}
            </h3>

            <div className="space-y-1">
              {product.ws_code && <p className="text-xs text-gray-600">WS: {product.ws_code}</p>}
              {product.category?.name && <p className="text-xs text-gray-500">Category: {product.category.name}</p>}
            </div>

            <div className="flex items-baseline space-x-2">
              <span className="text-lg font-bold text-springbok-600">R{displayPrice.toFixed(2)}</span>
              {originalPrice && <span className="text-sm text-gray-500 line-through">R{originalPrice.toFixed(2)}</span>}
            </div>
          </div>

          <Button
            onClick={handleAddToCart}
            disabled={!product.stock_available}
            className="w-full mt-3 text-xs sm:text-sm py-1.5 sm:py-2 px-2"
            data-gradient="green"
          >
            <ShoppingCart size={12} className="mr-1" />
            <span className="truncate">{product.stock_available ? "Add to Cart" : "Out of Stock"}</span>
          </Button>
        </CardContent>
      </Card>
    </Link>
  );
};

export default ProductCard;
